import { useCallback, useEffect, useMemo, useState } from "react";
import { demoApi } from "@/api/client";
import type { Equipment, Reminder } from "@/api/types";
import { describeReadFailure } from "@/lib/apiFailure";

export type EquipmentReadStatus = "loading" | "ready" | "error";

export interface UseEquipmentResult {
  /** `null` until the first read resolves, and `null` if it failed — never `[]` for "unknown". */
  items: Equipment[] | null;
  status: EquipmentReadStatus;
  error: string | null;
  /** Re-reads the list. Keeps the current rows on screen while it runs (no flash to "loading"). */
  refetch: () => void;
}

/**
 * ef-061-8 — the tenant's equipment from GET /api/demo/equipment, name order (the server's).
 * `includeArchived` is part of the request, so toggling "Show archived" is a fresh read, not a
 * client-side filter over stale rows.
 */
export function useEquipment(includeArchived: boolean): UseEquipmentResult {
  const [items, setItems] = useState<Equipment[] | null>(null);
  const [status, setStatus] = useState<EquipmentReadStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    demoApi
      .listEquipment(includeArchived, controller.signal)
      .then((rows) => {
        setItems(rows);
        setError(null);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setItems(null);
        setError(describeReadFailure(err));
        setStatus("error");
      });
    return () => controller.abort();
  }, [includeArchived, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { items, status, error, refetch };
}

export interface UseOpenRemindersResult {
  /** Open reminders grouped by the machine they belong to, most urgent first within each. */
  byEquipment: ReadonlyMap<string, Reminder[]>;
  status: EquipmentReadStatus;
  error: string | null;
  refetch: () => void;
}

/**
 * ef-061-8 — every open reminder (GET /api/demo/reminders, most urgent first), grouped per machine.
 * ONE read for the whole page, not one per machine; the page re-reads it after every write (logging
 * hours can flip a reminder to due at once, completing one re-arms or retires it) — the server's
 * answer is the truth, nothing is patched in place.
 */
export function useOpenReminders(): UseOpenRemindersResult {
  const [rows, setRows] = useState<Reminder[]>([]);
  const [status, setStatus] = useState<EquipmentReadStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    demoApi
      .listReminders({}, controller.signal)
      .then((list) => {
        setRows(list);
        setError(null);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeReadFailure(err));
        setStatus("error");
      });
    return () => controller.abort();
  }, [tick]);

  const byEquipment = useMemo(() => {
    const map = new Map<string, Reminder[]>();
    for (const r of rows) {
      if (!r.equipmentId) continue;
      const list = map.get(r.equipmentId);
      if (list) list.push(r);
      else map.set(r.equipmentId, [r]);
    }
    return map;
  }, [rows]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { byEquipment, status, error, refetch };
}

/** Today as `yyyy-mm-dd` in the farm's own (browser-local) calendar — never UTC, which is tomorrow by evening. */
export function localToday(): string {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
